import assert from 'assert';
import {useCallback, useEffect, useMemo, useState} from 'react';

import type {ExperimentMethod} from 'studies/typed/shared/ExperimentalInputs';
import {isDraftConditionGroup, type ConditionGroup} from 'studies/typed/shared/useExperimentDesignState';

import type {MethodIdAndName} from './activeMethodUtils';
import {computeSourceAndDownstreamNodeIds} from './ProcedureRunDesignFlowchartLayoutUtils';
import type {ProcedureRunDesignFlowchartNodeActionsContextValue} from './procedureRunDesignFlowchartNodeActionsContext';
import {parseProcedureRunDesignFlowchartNodeId} from './ProcedureRunDesignFlowchartNodeIdUtils';

// Reuse the same empty set to avoid unnecessary re-renders.
const EMPTY_SET: ReadonlySet<string> = new Set();

/** Labels describing the condition replicate being removed, used to build the confirmation modal copy. */
export interface RemovalConfirmationLabels {
  /** The full "{method} - {condition} - {replicate}" label for the originally-clicked node. */
  nodeLabel: string;
  /** The "{condition} - {replicate}" label shared by the node and downstream nodes. */
  conditionReplicateLabel: string;
}

export interface RemovalConfirmation {
  nodeIdsToRemove: ReadonlySet<string>;
  labels: RemovalConfirmationLabels;
  onConfirm: () => void;
  onCancel: () => void;
}

interface RemovalConfirmationState {
  sourceId: string;
  labels: RemovalConfirmationLabels;
}

export interface UseConditionReplicateRemovalArgs {
  conditions: Array<ConditionGroup>;
  methods: Array<ExperimentMethod>;
  isEditable: boolean;
  /** Optional because this callback is only needed when the condition replicate is unpersisted. */
  onRemoveConditionReplicate?: (conditionIndex: number, replicateIndex: number) => void;
}

export interface UseConditionReplicateRemovalResult {
  pendingRemovalNodeIds: ReadonlySet<string>;
  removedNodeIds: ReadonlySet<string>;
  nodeActionsContextValue: ProcedureRunDesignFlowchartNodeActionsContextValue;
  /** The in-progress removal confirmation. Null when no confirmation is open. */
  confirmation: RemovalConfirmation | null;
}

export function useConditionReplicateRemoval({
  conditions,
  methods,
  isEditable,
  onRemoveConditionReplicate,
}: UseConditionReplicateRemovalArgs): UseConditionReplicateRemovalResult {
  const [hoveredSourceId, setHoveredSourceId] = useState<string | null>(null);
  const [confirmationState, setConfirmationState] = useState<RemovalConfirmationState | null>(null);
  const [removedNodeIds, setRemovedNodeIds] = useState<ReadonlySet<string>>(EMPTY_SET);

  const getDownstreamNodeIds = useCallback(
    (sourceId: string | null): ReadonlySet<string> =>
      sourceId == null ? EMPTY_SET : computeSourceAndDownstreamNodeIds(sourceId, conditions, methods),
    [conditions, methods]
  );

  const confirmingNodeIds = useMemo(
    () => getDownstreamNodeIds(confirmationState?.sourceId ?? null),
    [getDownstreamNodeIds, confirmationState]
  );

  const pendingRemovalNodeIds = useMemo(() => {
    const hovered = getDownstreamNodeIds(hoveredSourceId);
    if (hovered.size === 0 && confirmingNodeIds.size === 0) {
      return EMPTY_SET;
    }
    return new Set<string>([...hovered, ...confirmingNodeIds]);
  }, [getDownstreamNodeIds, hoveredSourceId, confirmingNodeIds]);

  const nodeActionsContextValue = useMemo<ProcedureRunDesignFlowchartNodeActionsContextValue>(
    () => ({
      onHoverTrashIcon: (nodeId) => {
        setHoveredSourceId(nodeId);
      },
      onUnhoverTrashIcon: () => {
        setHoveredSourceId(null);
      },
      onClickTrashIcon: (nodeId) => {
        setConfirmationState({
          sourceId: nodeId,
          labels: getRemovalLabels(nodeId, conditions, methods),
        });
      },
    }),
    [conditions, methods]
  );

  const onConfirm = useCallback(() => {
    if (confirmationState == null) {
      return;
    }

    const {conditionIndex, replicateIndex} = parseProcedureRunDesignFlowchartNodeId(
      confirmationState.sourceId
    );
    const condition = conditions[conditionIndex];
    if (condition != null && isDraftConditionGroup(condition) && onRemoveConditionReplicate != null) {
      onRemoveConditionReplicate(conditionIndex, replicateIndex);
      setConfirmationState(null);
      setHoveredSourceId(null);
      return;
    }

    setRemovedNodeIds((current) => new Set<string>([...current, ...confirmingNodeIds]));
    setConfirmationState(null);
    setHoveredSourceId(null);
  }, [conditions, confirmingNodeIds, confirmationState, onRemoveConditionReplicate]);

  const onCancel = useCallback(() => {
    setConfirmationState(null);
  }, []);

  const confirmation = useMemo<RemovalConfirmation | null>(
    () =>
      confirmationState == null
        ? null
        : {nodeIdsToRemove: confirmingNodeIds, labels: confirmationState.labels, onConfirm, onCancel},
    [confirmationState, confirmingNodeIds, onConfirm, onCancel]
  );

  useEffect(() => {
    if (!isEditable) {
      setHoveredSourceId(null);
      setConfirmationState(null);
      setRemovedNodeIds(EMPTY_SET);
    }
  }, [isEditable]);

  return {
    pendingRemovalNodeIds,
    removedNodeIds,
    nodeActionsContextValue,
    confirmation,
  };
}

function getRemovalLabels(
  sourceId: string,
  conditions: Array<ConditionGroup>,
  methods: Array<MethodIdAndName>
): RemovalConfirmationLabels {
  const {conditionIndex, replicateIndex, methodId} = parseProcedureRunDesignFlowchartNodeId(sourceId);
  const condition = conditions[conditionIndex];
  const method = methods.find((m) => m.id === methodId);
  assert(condition != null, `Expected flowchart node ${sourceId} to have a matching condition`);
  assert(method != null, `Expected flowchart node ${sourceId} to have a matching method`);
  const conditionReplicateLabel = `${condition.name} - ${replicateIndex + 1}`;
  return {nodeLabel: `${method.name} - ${conditionReplicateLabel}`, conditionReplicateLabel};
}
